import React from 'react';
import { asyncConnect, withSagas, toSuccess } from 'react-async-client';
import { assoc, map } from 'ramda';
import { Spin } from 'antd';
import { takeEvery} from 'redux-saga/effects';

import {
    getCalendar, createTask, updateEvent, createSotrMeet,
    updateTask, updateSotrMeet, getEducationTimesheet, getTrainerTimesheet
} from 'actions/asyncActions';
import { fullAutoOptions } from 'constants/asyncClient';
import { spinDelay } from 'constants/defaults';
import { DATE_FORMATS } from 'constants/dateFormats'
import { periodConfig } from 'constants/calendar';
import ErrorHTML from 'components/common/form/ErrorHTML';
import TaskList from './Tasks/TaskList';
import EventList from './Events/EventList';
import CalendarFilters from './HeadPanel/CalendarFilters';
import withTranslateChange from 'components/hocs/withTranslateChange';
import { notEmpty } from 'utils/ramdaAdditions';

const getTransformedTimesheet = (days = [], isTrainer) => days.reduce((transformed, item) => {
    const items = isTrainer ? map(assoc('key', 'trainer'), item.items) : item.items;
    return assoc(item.date, [
        ...transformed[item.date] || [],
        ...items
    ], transformed);
}, {});

function CalendarList(props) {
    const { getCalendar: { meta, refresh, data }, filters,  planner, filterHandlers, education } = props;
    const { showCompletedTasks, onShowCompletedTasksToggle, date, period } = props;
    const { getEducationTimesheet: { data: educationData, meta: educationMeta } } = props;
    const { getTrainerTimesheet: { data: trainerData, meta: trainerMeta } } = props;
    const { user: { id: currentId }, userId: ownerId } = props;
    const isAllowedToEdit = currentId === parseInt(ownerId, 10);
    const timesheetDays = getTransformedTimesheet(educationData.days);
    const trainerDays = getTransformedTimesheet(trainerData.days, true);
    const educationPermitted = !!props.user.permissions.education;

    return (
        <div>
            <CalendarFilters
                filters={filters}
                refreshMeta={meta}
                handleRefresh={() => refresh()}
                planner={planner}
                education={education}
                filterHandlers={filterHandlers}
                date={date}
                period={period}
                educationPermitted={educationPermitted}
            />
            <ErrorHTML meta={meta} refresh={refresh} />
            <Spin spinning={meta.pending || educationMeta.pending || trainerMeta.pending} delay={spinDelay} className="full-width">
                {(notEmpty(data) || notEmpty(educationData) || notEmpty(trainerDays)) && (
                    <EventList
                        days={data.events || educationData.days || trainerDays.days}
                        timesheetDays={timesheetDays}
                        trainerDays={trainerDays}
                        filters={filters}
                        planner={planner}
                        education={education}
                        period={period}
                        currentId={currentId}
                        isAllowedToEdit={isAllowedToEdit}
                    />
                )}
                {notEmpty(data) && (
                    <TaskList
                        tasks={data.tasks}
                        showCompletedTasks={showCompletedTasks}
                        onShowCompletedTasksToggle={onShowCompletedTasksToggle}
                        isAllowedToEdit={isAllowedToEdit}
                    />
                )}
            </Spin>
        </div>
    );
}

const calendarRefresh = ({ user, getCalendar: { refresh } }) => user.permissions.web && refresh();
const withTranslateCalendar = withTranslateChange(calendarRefresh)(CalendarList);

const getCalendarPayload = ({ userId, period, date }) => {
    const { dateFrom, dateTo } = periodConfig[period].getFromToObj(date);

    return {
        userId,
        datefrom: dateFrom.format(DATE_FORMATS.parse),
        dateto: dateTo && dateTo.format(DATE_FORMATS.parse)
    };
};

export default asyncConnect({
    getCalendar: getCalendar
        .withPayload(getCalendarPayload)
        .withOptions(({ user }) => user.permissions.web && fullAutoOptions),
    getEducationTimesheet: getEducationTimesheet
        .withParams('participant')
        .withPayload(props => ({
            ...getCalendarPayload(props),
            participant: 1,
            trainer: 0,
            calendar: true,
        }))
        .withOptions(({ user }) => (
            user.permissions.education && fullAutoOptions
        )),
    getTrainerTimesheet: getTrainerTimesheet
        .withParams('trainer')
        .withPayload(props => ({
            ...getCalendarPayload(props),
            participant: 0,
            trainer: 1,
            calendar: true,
        }))
        .withOptions(({ user }) => (
            user.permissions.web.trainerEvents && fullAutoOptions
        )),
})(withSagas([function* (getProps) {
    yield takeEvery([
        toSuccess(updateEvent.type),
        toSuccess(createTask.type),
        toSuccess(updateTask.type),
        toSuccess(createSotrMeet.type),
        toSuccess(updateSotrMeet.type),
    ], () => {
        calendarRefresh(getProps());
    });
}])(withTranslateCalendar));
